// src/pages/TeacherLivePage.jsx
import React, { useMemo, useState } from "react";

// Demo testlar (backend o'rniga)
const DEMO_TESTS = [
  {
    id: "t1",
    title: "Matematika 5-sinf",
    totalQuestions: 3,
    questions: [
      {
        text: "Kasr sonlar qaysi sinfda o‘rganiladi?",
        options: ["1-sinf", "3-sinf", "5-sinf", "9-sinf"],
        correctIndex: 2,
      },
      {
        text: "1/2 ning 2/2 ga nisbati qanday?",
        options: ["1", "2", "1/2", "4"],
        correctIndex: 0,
      },
      {
        text: "0,25 qaysi kasrga teng?",
        options: ["1/2", "1/3", "1/4", "1/5"],
        correctIndex: 2,
      },
    ],
  },
  {
    id: "t2",
    title: "Tarix 8-sinf",
    totalQuestions: 2,
    questions: [
      {
        text: "Ikkinchi jahon urushi qaysi yillarda bo‘lib o‘tgan?",
        options: [
          "1914–1918-yillar",
          "1939–1945-yillar",
          "1870–1871-yillar",
          "1961–1975-yillar",
        ],
        correctIndex: 1,
      },
      {
        text: "O‘zbekiston qachon mustaqillikka erishgan?",
        options: [
          "1989-yil 21-iyun",
          "1990-yil 20-mart",
          "1991-yil 31-avgust",
          "1995-yil 1-yanvar",
        ],
        correctIndex: 2,
      },
    ],
  },
];

// Demo ism ro'yxati — qatnashuvchilar qo'shish uchun
const DEMO_NAMES = ["Ali", "Vali", "Aziza", "Laylo", "Bobur", "Dilnoza"];

function randomCode() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

export default function TeacherLivePage() {
  const [selectedTestId, setSelectedTestId] = useState("");
  const [session, setSession] = useState(null);
  const [statusText, setStatusText] = useState("");

  const selectedTest = useMemo(
    () => DEMO_TESTS.find((t) => t.id === selectedTestId) || null,
    [selectedTestId]
  );

  const isLobby = session?.status === "LOBBY";
  const isRunning = session?.status === "RUNNING";
  const isFinished = session?.status === "FINISHED";

  const currentQuestion =
    session && session.test.questions[session.currentQuestionIndex];

  const currentPlayer =
    session &&
    session.participants.find(
      (p) => p.positionInQueue === session.currentPlayerIndex
    );

  const sortedParticipants = useMemo(() => {
    if (!session) return [];
    return [...session.participants].sort((a, b) => b.score - a.score);
  }, [session]);

  const top3 = sortedParticipants.slice(0, 3);

  /* ---------- HANDLERS ---------- */

  const handleCreateSession = () => {
    if (!selectedTest) return;
    setStatusText("");

    setSession({
      code: randomCode(),
      status: "LOBBY",
      currentQuestionIndex: 0,
      currentPlayerIndex: 0,
      participants: [],
      test: selectedTest,
    });
  };

  const handleAddDemoParticipant = () => {
    if (!session) return;

    const usedNames = session.participants.map((p) => p.name);
    const available = DEMO_NAMES.filter((n) => !usedNames.includes(n));
    const name =
      available.length > 0
        ? available[0]
        : `O‘quvchi ${session.participants.length + 1}`;

    const newParticipant = {
      name,
      score: 0,
      positionInQueue: session.participants.length,
    };

    setSession((prev) => ({
      ...prev,
      participants: [...prev.participants, newParticipant],
    }));
  };

  const handleStart = () => {
    if (!session) return;
    if (session.participants.length === 0) {
      setStatusText("Kamida bitta qatnashuvchi bo‘lishi kerak.");
      return;
    }
    setStatusText("");

    setSession((prev) => ({
      ...prev,
      status: "RUNNING",
      currentQuestionIndex: 0,
      currentPlayerIndex: 0,
    }));
  };

  // Backend / student o'rnini bosadigan bitta funksiya:
  // "Navbatdagi o‘quvchi javob berdi" tugmasi bosilganda
  const handleSimulateAnswer = () => {
    if (!session || !currentQuestion) return;

    setSession((prev) => {
      const participants = [...prev.participants].sort(
        (a, b) => a.positionInQueue - b.positionInQueue
      );
      const current = participants[prev.currentPlayerIndex];

      if (!current) return prev;

      // Tasodifiy to‘g‘ri / noto‘g‘ri javob
      const isCorrect = Math.random() < 0.7; // 70% to'g'ri qilsin
      const updatedParticipants = prev.participants.map((p) =>
        p.name === current.name
          ? { ...p, score: p.score + (isCorrect ? 100 : 0) }
          : p
      );

      let nextQuestionIndex = prev.currentQuestionIndex;
      let nextPlayerIndex = prev.currentPlayerIndex;

      if (nextPlayerIndex + 1 < participants.length) {
        nextPlayerIndex += 1;
      } else {
        nextPlayerIndex = 0;
        nextQuestionIndex += 1;
      }

      let nextStatus = prev.status;
      if (nextQuestionIndex >= prev.test.questions.length) {
        nextStatus = "FINISHED";
      }

      return {
        ...prev,
        participants: updatedParticipants,
        currentQuestionIndex: nextQuestionIndex,
        currentPlayerIndex: nextPlayerIndex,
        status: nextStatus,
      };
    });
  };

  const handleReset = () => {
    setSession(null);
    setSelectedTestId("");
    setStatusText("");
  };

  /* ---------- UI ---------- */

  return (
    <div className="space-y-8">
      {/* Title + kod badge */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Jonli test</h1>
          <p className="text-sm text-gray-500">
            Testni tanlang, o‘quvchilarga kod bering va navbat bilan onlayn
            quiz o‘tkazing. Hozircha barcha logika faqat front ichida
            simulyatsiya qilinyapti.
          </p>
        </div>

        {session?.code && (
          <div className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm">
            Kirish kodi:{" "}
            <span className="font-semibold tracking-widest">
              {session.code}
            </span>
          </div>
        )}
      </div>

      {/* 1-qadam: Test tanlash (sessiya yo'q payt) */}
      {!session && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">
            1-qadam • Testni tanlang
          </h2>

          <select
            className="w-full border border-gray-200 rounded-2xl px-3 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-1 focus:ring-black"
            value={selectedTestId}
            onChange={(e) => setSelectedTestId(e.target.value)}
          >
            <option value="">Testni tanlang</option>
            {DEMO_TESTS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title} ({t.questions.length} ta savol)
              </option>
            ))}
          </select>

          <button
            onClick={handleCreateSession}
            disabled={!selectedTest}
            className="mt-4 w-full rounded-full bg-gray-900 text-white text-sm py-3 disabled:opacity-50"
          >
            Sessiya yaratish
          </button>

          {statusText && (
            <p className="text-xs text-red-500 mt-2">{statusText}</p>
          )}
        </div>
      )}

      {/* Sessiya yaratilgan holat */}
      {session && (
        <div className="space-y-6">
          {/* Sessiya bosh paneli */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Sessiya holati
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {isLobby && "Kutish (o‘quvchilar qo‘shilmoqda)"}
                  {isRunning && "Test davom etmoqda"}
                  {isFinished && "Test tugadi"}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Test: {session.test.title} —{" "}
                  {session.test.questions.length} ta savol
                </p>
              </div>

              <div className="flex items-center gap-2">
                {isLobby && (
                  <button
                    onClick={handleStart}
                    disabled={session.participants.length === 0}
                    className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm disabled:opacity-40"
                  >
                    Testni boshlash
                  </button>
                )}

                {isFinished && (
                  <button
                    onClick={handleReset}
                    className="px-3 py-2 rounded-full border border-gray-200 text-xs text-gray-700"
                  >
                    Yangi sessiya
                  </button>
                )}
              </div>
            </div>

            {/* Qatnashuvchilar bloki */}
            <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
              <span>
                Qatnashuvchilar:{" "}
                <span className="font-semibold">
                  {session.participants.length} ta
                </span>
              </span>

              {isLobby && (
                <button
                  type="button"
                  onClick={handleAddDemoParticipant}
                  className="px-3 py-1.5 rounded-full border border-gray-200 text-xs hover:bg-gray-50"
                >
                  Demo qatnashuvchi qo‘shish
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {session.participants.map((p) => (
                <div
                  key={p.name}
                  className="px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                >
                  {p.positionInQueue + 1}. {p.name} —{" "}
                  <span className="font-semibold">{p.score} ball</span>
                </div>
              ))}
              {session.participants.length === 0 && (
                <p className="text-xs text-gray-400">
                  Hali hech kim qo‘shilmagan. Hozircha demo sifatida
                  o‘qituvchi o‘zi “Demo qatnashuvchi qo‘shish” tugmasini
                  bosib ko‘rishi mumkin.
                </p>
              )}
            </div>

            {statusText && (
              <p className="text-xs text-red-500 mt-1">{statusText}</p>
            )}
          </div>

          {/* Test jarayoni (savol + navbat) */}
          {isRunning && currentQuestion && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Savol{" "}
                  <span className="font-semibold">
                    {session.currentQuestionIndex + 1} /{" "}
                    {session.test.questions.length}
                  </span>
                </p>
                {currentPlayer && (
                  <p className="text-xs text-gray-500">
                    Hozir navbat:{" "}
                    <span className="font-semibold">
                      {currentPlayer.name}
                    </span>
                  </p>
                )}
              </div>

              <p className="text-sm font-medium text-gray-900">
                {currentQuestion.text}
              </p>

              <div className="space-y-2">
                {currentQuestion.options.map((opt, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  >
                    <span className="mr-2 text-xs font-semibold text-gray-400">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    {opt}
                  </div>
                ))}
              </div>

              <div className="pt-3">
                <button
                  type="button"
                  onClick={handleSimulateAnswer}
                  className="px-4 py-2 rounded-full bg-gray-900 text-white text-xs"
                >
                  Navbatdagi o‘quvchi javob berdi (demo)
                </button>
                <p className="mt-1 text-[11px] text-gray-400">
                  Hozircha student tomoni yo‘qligi uchun bu tugma test
                  jarayonini front ichida simulyatsiya qiladi.
                </p>
              </div>
            </div>
          )}

          {/* Natijalar paneli */}
          {isFinished && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-4">
              <h2 className="text-sm font-semibold text-gray-800">
                Natijalar (demo)
              </h2>

              <div className="space-y-2 text-sm">
                {top3.map((p, idx) => (
                  <div key={p.name} className="flex justify-between">
                    <span>
                      {idx + 1}. {p.name}
                    </span>
                    <span className="font-semibold">{p.score} ball</span>
                  </div>
                ))}
                {top3.length === 0 && (
                  <p className="text-xs text-gray-400">
                    Qatnashuvchilar yo‘q, shuning uchun natija ham yo‘q.
                  </p>
                )}
              </div>

              <details className="mt-3 text-xs text-gray-500">
                <summary className="cursor-pointer">
                  Barcha qatnashuvchilarni ko‘rish
                </summary>
                <div className="mt-2 space-y-1">
                  {sortedParticipants.map((p, idx) => (
                    <div
                      key={p.name}
                      className="flex justify-between text-xs py-1 border-b border-gray-100"
                    >
                      <span>
                        {idx + 1}. {p.name}
                      </span>
                      <span>{p.score} ball</span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
